import { createApi } from '@reduxjs/toolkit/query/react'
import { createClient } from 'contentful'

import {
  CONTENTFUL_ACCESS_TOKEN,
  CONTENTFUL_ENVIRONMENT,
  CONTENTFUL_HOST,
  CONTENTFUL_SPACE_ID,
} from 'consts'
import {
  parseArticlesData,
  parseKnowledgeBaseData,
} from 'containers/contentful/utils'

export const client = createClient({
  space: CONTENTFUL_SPACE_ID,
  accessToken: CONTENTFUL_ACCESS_TOKEN,
  environment: CONTENTFUL_ENVIRONMENT,
  host: CONTENTFUL_HOST,
})

export const contentfulApi = createApi({
  reducerPath: 'contentfulApi',
  baseQuery: async args => {
    try {
      const response = await client.getEntries(args)
      return { data: response }
    } catch (error) {
      return { error }
    }
  },
  endpoints: build => ({
    getDocuments: build.query({
      query: ({ contentfulSlug }) => ({
        'fields.slug': contentfulSlug,
        content_type: 'documentsPage',
      }),
      transformResponse: response => {
        const entries = response.includes.Entry
        const assets = response.includes.Asset

        return response.items[0].fields.documents.map(document => {
          const entry = entries.find(item => item.sys.id === document.sys.id)
          return {
            id: entry.sys.id,
            title: entry.fields.displayName,
            currentFileUrl: assets.find(
              asset => entry.fields.currentFile.sys.id === asset.sys.id,
            ).fields.file.url,
            previousVersionFileUrl: assets.find(
              asset =>
                entry.fields.previousVersionFile?.sys.id === asset.sys.id,
            )?.fields.file.url,
          }
        })
      },
    }),
    getMaintenanceConfig: build.query({
      query: ({ contentfulSlug }) => ({
        content_type: 'maintenanceToast',
        include: 1,
        'fields.id': contentfulSlug,
      }),
      transformResponse: response => {
        if (!response.items || response.items.length === 0) return null

        const toast = response.items[0]
        const includes = response.includes || {}

        const themeLink = toast.fields.theme
        const theme = includes.Entry?.find(
          entry =>
            entry.sys.id === themeLink.sys.id &&
            entry.sys.contentType.sys.id === 'maintenanceToastTheme',
        )

        return {
          text: toast.fields.body,
          theme: theme
            ? {
                color: theme.fields.fontColor,
                backgroundColor: theme.fields.backgroundColor,
              }
            : null,
        }
      },
    }),
    getKnowledgeBase: build.query({
      query: () => ({
        include: 3,
        content_type: 'knowledgeBase',
        'fields.platform': 'web',
      }),
      transformResponse: parseKnowledgeBaseData,
    }),
    searchArticles: build.query({
      query: ({ query, categories }) => ({
        content_type: 'knowledgeBaseArticle',
        query,
        'sys.id[in]': categories.reduce((acc, item) => {
          let newRes = acc
          item.articles.forEach(article => {
            newRes = `${newRes}${article.articleId},`
          })
          return newRes
        }, ''),
      }),
      transformResponse: (response, meta, { categories, query }) => {
        if (!categories) return []

        const articles = parseArticlesData(response)

        const articleIds = articles.map(article => article.articleId)

        const newCategories = categories.reduce((acc, category) => {
          const newCategory = {
            ...category,
            articles: category.articles.filter(article =>
              articleIds.includes(article.articleId),
            ),
          }
          if (newCategory.articles.length) {
            return [...acc, newCategory]
          }

          return acc
        }, [])

        return {
          categories: newCategories,
          isFiltered: !!query,
        }
      },
    }),
  }),
})

export const {
  useGetDocumentsQuery,
  useGetMaintenanceConfigQuery,
  useGetKnowledgeBaseQuery,
  useSearchArticlesQuery,
} = contentfulApi
